import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { buildBlueprintContext, PageBrief, ClientEntity } from '../../drydock/orchestrator/blueprint-context';
import { BlueprintContext } from '../../drydock/orchestrator/blueprint.types';
import { EntityService } from './entity.service';
import { GlobalVariablesService } from './global-variables.service';
import { Page } from '../model/page.model';

/**
 * Assemble the `BlueprintContext` for an editor-driven generation.
 *
 * ─── WHY THIS EXISTS ──────────────────────────────────────────────────────────────────────────────
 * `blueprintIterate` accepts a context and the editor never sent one. The whole interactivity layer is
 * gated on it — `buildSingleShotTextPrompt({ interactivity: !!context })` — so a page regenerated from
 * the editor was generated with no page registry, no globals and no data model, while the system
 * prompt went on asserting "The registry below is COMPLETE".
 *
 * Blueprint did exactly what it was told: it looked for the destination in an empty registry, did not
 * find it, and marked the control `inert` with a note saying so. Measured 2026-08-20 across 34
 * AI-generated pages: 6 pages carried 20 dead controls, every one of them last generated through this
 * path — `homePage` alone lost five, including the My Farm tile, whose handler a build run had
 * previously created. A regenerate silently downgraded working navigation to inert.
 *
 * The orchestrator has assembled this context since it was written; this is the same call, with the
 * briefs read from the client's existing pages instead of a spec file.
 *
 * ─── WHAT EACH PAGE CONTRIBUTES ───────────────────────────────────────────────────────────────────
 * `purpose` and `primaryEntity` are what make a registry entry worth more than a name — they are how
 * another page's intent can say WHY it would navigate here. Both are already stored: `description` on
 * the page doc (added for exactly this, the NAVIGATE lane's own description), and `interactions`
 * carries `purpose`/`primaryEntity`/`archetype` from the generation that produced the page.
 */
@Injectable({ providedIn: 'root' })
export class BlueprintContextBuilder {
  private entityService = inject(EntityService);
  private globalVariablesService = inject(GlobalVariablesService);

  /**
   * @param pages    every page on the client — the registry is the CLOSED set of navigation targets,
   *                 so a page missing here is a page Blueprint may not name.
   * @param forName  the page being generated. A page being CREATED is not in `pages` yet, so it is
   *                 matched by name and synthesised when absent.
   * @param brief    the description the builder just typed, for the page being generated.
   */
  async build(pages: Page[], forName: string, brief?: string): Promise<BlueprintContext | undefined> {
    // Both services load asynchronously per client. Building before they land would send an EMPTY
    // registry, which is the precise failure this file exists to fix — and an empty registry is worse
    // than none, because the prompt presents it as complete.
    await Promise.all([this.entityService.ready(), this.globalVariablesService.ready()]);

    const entities = this.toClientEntities(await firstValueFrom(this.entityService.$values));
    const briefs = (pages || []).map((p) => this.toBrief(p));
    if (!briefs.length && !forName) return undefined;

    const mine = briefs.find((b) => b.pageName === forName || b.pageId === forName);
    // A page being created has no doc yet — it still belongs in its own context, and it still needs a
    // brief. `primaryEntity` is unknown until Blueprint picks one, so the data model comes back empty
    // for a NEW page while the registry and globals arrive in full: navigation is fixed either way,
    // and entity bindings are fixed on every regenerate.
    const forPage: PageBrief = mine
      ? { ...mine, brief: brief || mine.brief }
      : { pageId: forName, pageName: forName, brief: brief || '' };

    const registry = mine ? briefs : [...briefs, forPage];
    const globals = this.globalVariablesService.globalVariable;
    try {
      // `catalogue` — every entity, unscoped, for Prism — is attached by buildBlueprintContext
      // itself, so this call site and the Drydock harness get it identically.
      return buildBlueprintContext(entities, registry, forPage, globals);
    } catch (e) {
      // `entitiesForPage` THROWS on a primaryEntity that no longer exists — right for the orchestrator,
      // where a spec typo should fail the build, and wrong here. A page's stored `primaryEntity` is a
      // fact about the generation that made it, and the entity behind it can be renamed or deleted at
      // any time afterwards. Failing the whole generation over that would be a worse bug than the one
      // this class fixes.
      //
      // So degrade instead of dying: drop the data model and send the registry and globals anyway.
      // Navigation — the thing that was actually broken — keeps working; only entity bindings are lost,
      // and they were already lost before this class existed.
      console.warn('[blueprint-context] data model omitted:', (e as Error)?.message);
      return buildBlueprintContext(entities, registry, { ...forPage, primaryEntity: undefined }, globals);
    }
  }

  /** The editor's `Entity` shaped as the builder's `ClientEntity`. `data` is the field array — NOT `fields`. */
  private toClientEntities(loaded: any[]): ClientEntity[] {
    return (loaded || []).map((e: any) => ({
      id: e.id,
      name: e.name,
      description: e.description,
      data: e.data,
      status: e.status,
    }));
  }

  private toBrief(p: Page): PageBrief {
    const i: any = (p as any).interactions || {};
    return {
      pageId: (p as any).id,
      pageName: p.name,
      // The page's OWN description is the authored one; interactions.purpose is what Blueprint wrote
      // about itself last time. Prefer the authored line, fall back rather than leaving the entry bare.
      brief: p.description || i.purpose || '',
      ...(p.description || i.purpose ? { purpose: p.description || i.purpose } : {}),
      ...(i.primaryEntity ? { primaryEntity: i.primaryEntity } : {}),
      ...(i.archetype ? { archetype: i.archetype } : {}),
    };
  }
}
